import React, { useCallback, useState, type FormEvent } from 'react';
import styled from 'styled-components';
import { Button, Input, Message } from 'semantic-ui-react';
import { Customers } from './customers';
import { findCustomerByEmail } from '../../../api/index';
import { Customer } from '../../../types/models/customer';

const Wrapper = styled.div`
  padding: 2em;
  max-width: 700px;
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-gap: 0.5em;
  margin-bottom: 1.5em;
`;

/**
 * Customer lookup.
 *
 * Deliberately requires a full email address and searches only on submit. The
 * previous version fired a substring search on every keystroke against a
 * healthcare provider's patient list; typing one letter returned thousands of
 * records. Exact match is what the server offers now, and it is the right
 * amount of access for the job this screen does — finding one person who has
 * asked about their account.
 */
export function CustomerListPage() {
  const [email, setEmail] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [searched, setSearched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = useCallback(
    async (event?: FormEvent) => {
      event?.preventDefault();
      const value = email.trim();
      if (!value) return;

      setBusy(true);
      setError(null);
      try {
        const response: any = await findCustomerByEmail(value);
        setCustomers(response?.data ?? []);
        setSearched(true);
      } catch {
        setError('Vyhledávání selhalo.');
      } finally {
        setBusy(false);
      }
    },
    [email]
  );

  return (
    <Wrapper>
      <Form onSubmit={search}>
        <Input
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setSearched(false);
          }}
          icon="search"
          iconPosition="left"
          placeholder="celá e-mailová adresa"
        />
        <Button primary type="submit" loading={busy} disabled={busy || !email.trim()}>
          Najít
        </Button>
      </Form>

      {error ? <Message negative content={error} /> : null}
      {searched && customers.length === 0 && !error ? (
        <Message info content="Pro tuto adresu není žádný zákazník." />
      ) : null}

      <Customers customers={customers} onCustomerDeleted={() => void search()} />
    </Wrapper>
  );
}
